/**
 * MaisonRenderStack3D — V14.88 « Empilement 3D » (vanilla three.js).
 *
 * Exploded view des rendus d'étages : chaque rendu Shoebox est plaqué sur un
 * plan horizontal, empilé dans l'ordre des niveaux (sous-sol en bas →
 * combles en haut), avec un slider d'écartement. Vanilla THREE via
 * `useThreeViewer` (JAMAIS de React-Three-Fiber — cf. useThreeViewer.js).
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as THREE from 'three';
import { Layers3, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useThreeViewer, disposeGroup } from './useThreeViewer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLANE_W = 10;

const KIND_COLORS = {
  basement: '#94A3B8', ground: '#34D399', upper: '#38BDF8', attic: '#FBBF24',
};

const makeLabelSprite = (text, color) => {
  const c = document.createElement('canvas');
  c.width = 512; c.height = 112;
  const ctx = c.getContext('2d');
  ctx.fillStyle = 'rgba(10,14,20,0.82)';
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(0, 8, 512, 96, 20);
    ctx.fill();
  } else {
    ctx.fillRect(0, 8, 512, 96);
  }
  ctx.font = '600 52px Outfit, Arial, sans-serif';
  ctx.fillStyle = color;
  ctx.textBaseline = 'middle';
  ctx.fillText(text, 28, 58);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false }));
  sprite.scale.set(4.6, 1.0, 1);
  return sprite;
};

const StackViewer = ({ floors, spacing }) => {
  const layersRef = useRef([]);
  const spacingRef = useRef(spacing);
  spacingRef.current = spacing;

  const { canvasRef, stateRef } = useThreeViewer({
    deps: [floors],
    cameraInit: { pos: [15, 12, 15], target: [0, 3, 0] },
    background: '#0b1220',
    enableShadows: false,
    fov: 40,
    onCreate({ scene, controls }) {
      const group = new THREE.Group();
      scene.add(group);
      const grid = new THREE.GridHelper(30, 30, 0x233045, 0x18202e);
      grid.position.y = -0.6;
      group.add(grid);

      const loader = new THREE.TextureLoader();
      const layers = [];
      (floors || []).forEach((f, idx) => {
        const layer = new THREE.Group();
        layer.position.y = idx * spacingRef.current;
        const mat = new THREE.MeshBasicMaterial({
          color: 0x1c2430, side: THREE.DoubleSide, transparent: true, opacity: 0.98,
        });
        const mesh = new THREE.Mesh(new THREE.PlaneGeometry(PLANE_W, PLANE_W * 0.75), mat);
        mesh.rotation.x = -Math.PI / 2;
        layer.add(mesh);
        loader.load(`${process.env.REACT_APP_BACKEND_URL}${f.image_url}`, (tex) => {
          tex.colorSpace = THREE.SRGBColorSpace;
          const ar = tex.image && tex.image.width ? tex.image.height / tex.image.width : 0.75;
          mesh.geometry.dispose();
          mesh.geometry = new THREE.PlaneGeometry(PLANE_W, PLANE_W * ar);
          mat.map = tex;
          mat.color.set(0xffffff);
          mat.needsUpdate = true;
        });
        const label = makeLabelSprite(f.name || f.kind_label, KIND_COLORS[f.kind] || '#E2E8F0');
        label.position.set(-PLANE_W / 2 - 2.8, 0.4, 0);
        layer.add(label);
        group.add(layer);
        layers.push(layer);
      });
      layersRef.current = layers;
      if (controls && layers.length > 0) {
        controls.target.y = ((layers.length - 1) * spacingRef.current) / 2;
        controls.update();
      }
      return () => {
        layersRef.current = [];
        disposeGroup(group);
        scene.remove(group);
      };
    },
  });

  // Écartement : reposition sans reconstruire la scène (ni recharger les textures).
  useEffect(() => {
    layersRef.current.forEach((layer, i) => { layer.position.y = i * spacing; });
    const { controls } = stateRef.current || {};
    if (controls && layersRef.current.length > 0) {
      controls.target.y = ((layersRef.current.length - 1) * spacing) / 2;
      controls.update();
    }
  }, [spacing, floors, stateRef]);

  return <canvas ref={canvasRef} className="w-full h-full block" data-testid="maison-stack-canvas" />;
};

/**
 * MaisonStackButton — bouton « Empilement 3D » pour l'en-tête de la galerie.
 * Ne s'affiche que si le projet est une maison ET qu'au moins 2 étages ont
 * un rendu complété. Ouvre la vue exploded en dialog.
 */
export const MaisonStackButton = ({ projectId, refreshNonce = 0 }) => {
  const [floors, setFloors] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spacing, setSpacing] = useState(2.4);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/projects/${projectId}/maison/stack-renders`,
          { withCredentials: true },
        );
        if (!cancelled) setFloors(r.data?.is_maison ? (r.data.floors || []) : []);
      } catch { if (!cancelled) setFloors([]); }
    })();
    return () => { cancelled = true; };
  }, [projectId, refreshNonce]);

  const openStack = async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/projects/${projectId}/maison/stack-renders`,
        { withCredentials: true },
      );
      setFloors(r.data?.floors || []);
      setOpen(true);
    } finally { setLoading(false); }
  };

  if (!floors || floors.length < 2) return null;

  return (
    <>
      <button
        type="button"
        onClick={openStack}
        disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11.5px] font-medium transition-colors disabled:opacity-40"
        style={{
          background: 'rgba(217,160,91,0.10)',
          color: 'var(--c-gold)',
          border: '1px solid rgba(217,160,91,0.35)',
        }}
        title="Superpose les rendus des étages en vue éclatée pour vérifier la cohérence"
        data-testid="maison-stack-open-btn"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Layers3 className="w-3 h-3" strokeWidth={1.75} />}
        Empilement 3D
        <span className="ml-0.5 text-[10px] tabular-nums opacity-70">{floors.length}</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl bg-[#0d1117] border-white/10 text-white"
                       data-testid="maison-stack-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[15px]">
              <Layers3 className="w-4 h-4" style={{ color: 'var(--c-gold)' }} />
              Empilement 3D — cohérence inter-niveaux
            </DialogTitle>
            <DialogDescription className="text-[11.5px] text-gray-400">
              Dernier rendu de chaque étage, empilé du sous-sol aux combles. Faites pivoter
              (glisser) et zoomez (molette) pour vérifier l'alignement des trémies, façades
              et de l'environnement extérieur.
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg overflow-hidden border border-white/10" style={{ height: '58vh' }}>
            <StackViewer floors={floors} spacing={spacing} />
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[10.5px] uppercase tracking-wider text-gray-500">Écartement</label>
            <input
              type="range" min={0.6} max={5} step={0.1}
              value={spacing}
              onChange={(e) => setSpacing(parseFloat(e.target.value))}
              className="w-48 accent-amber-500"
              data-testid="maison-stack-spacing-slider"
            />
            <span className="text-[10.5px] text-gray-400 tabular-nums">{spacing.toFixed(1)}</span>
            <div className="ml-auto flex items-center gap-2 flex-wrap" data-testid="maison-stack-legend">
              {floors.map((f) => (
                <span key={f.floor_id}
                      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded"
                      style={{ background: `${KIND_COLORS[f.kind] || '#64748B'}18`, color: KIND_COLORS[f.kind] || '#94A3B8' }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: KIND_COLORS[f.kind] || '#94A3B8' }} />
                  {f.name}
                  {f.is_terrain_anchor && <span className="opacity-70">· ancre terrain</span>}
                </span>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
